import { useContext, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import * as dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import {
  BY_DATE,
  BY_RECENT,
  DEFAULT,
  defaultVimeoId,
  EN,
  serviceArchiveTitleString,
  serviceArchiveSubtitleString,
  serviceArchiveSelectPromptString,
  unavailableVideoString,
} from "../../constants";
import MessagePanel from "../../multiuseComponents/messagePanel/messagePanel";
import ServiceContext from "../../contexts/serviceContext";
import unavailable_en from "../../assets/video/Unavailable_EN.mp4";
import unavailable_ro from "../../assets/video/Unavailable_RO.mp4";
import ServiceLoading from "../../components/ServiceLoading";
import { services_endpoint } from "../../helpers/api/apiConstants";
import { getUrl } from "../../helpers/api/api";
import { useAppSelector } from "../../app/hooks";
import { selectLanguage } from "../../app/slices/languageSlice";

/**
 * A deliberately plain, mostly-unlinked page: the historical service
 * archive (2013 onward), for members who want to find an old service
 * rather than watch something recent. Reuses the exact same
 * MessagePanel/ServiceContext/Vimeo-timestamp-jump machinery as the main
 * Services page - see services.js - just without that page's "live/latest
 * service" framing, since there's no current service to default to here.
 */
const Vault = () => {
  const theme = useTheme();
  const { serviceId, setServiceId } = useContext(ServiceContext);
  const language = useAppSelector(selectLanguage);
  const location = useLocation();
  const [vimeoId, setVimeoId] = useState("");
  const [services, setServices] = useState();
  const [videoError, setVideoError] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(BY_RECENT);
  const [sortDate, setSortDate] = useState(dayjs());
  const [isMobile, setIsMobile] = useState(false);
  const [startSecond, setStartSecond] = useState("0");
  const [serviceStartTimestamp, setServiceStartTimestamp] = useState();
  const [servicePointTimestamp, setServicePointTimestamp] = useState();
  const [loadingInitial, setLoadingInitial] = useState(true);
  const isMountedRef = useRef(false);

  const getServices = async () => {
    fetch(
      getUrl(
        `${services_endpoint}?page=${page}${
          sortBy === BY_DATE ? `&date=${sortDate}` : ""
        }`
      )
    )
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      })
      .then((data) => {
        if (!data || data.length < 40) {
          setHasMore(false);
        }
        setServices(data);
        setLoadingInitial(false);
      })
      .catch((error) => {
        console.error("Error getting service data. Error: ", error);
      });
  };

  const getMoreServices = async () => {
    fetch(
      getUrl(
        `${services_endpoint}?page=${page}${
          sortBy === BY_DATE ? `&date=${sortDate}` : ""
        }`
      )
    )
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw response;
      })
      .then((data) => {
        if (!data || data.length < 40) {
          setHasMore(false);
        }
        const tmpServices = [...services];
        setServices([].concat(tmpServices, data));
      })
      .catch((error) => {
        console.error("Error getting service data. Error: ", error);
      });
  };

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 990);
  };

  useEffect(() => {
    getServices();
    setIsMounted(true);
    isMountedRef.current = true;
    handleResize();
    window.addEventListener("resize", handleResize);

    const handleMessage = (event) => {
      if (event.origin !== "https://player.vimeo.com") {
        return;
      }
      if (
        event.data.event === "error" &&
        event.data.data.message === "This video does not exist."
      ) {
        console.error("Vimeo video not found");
        setVideoError(true);
      }
    };

    window.addEventListener("message", handleMessage, false);

    return () => {
      setServices(null);
      setIsMounted(false);
      isMountedRef.current = false;
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    if (serviceId && services) {
      setVideoError(false);
      const service = services.find(
        (service) => service.SERVICE_KEY === serviceId
      );
      if (service) {
        setServiceStartTimestamp(service?.START_TIMESTAMP_UNIX);
        const vimeo_id = service?.VIMEO_ID;
        setVimeoId(vimeo_id !== DEFAULT ? vimeo_id : defaultVimeoId(language));
      }
    }
  }, [serviceId, services, language]);

  useEffect(() => {
    if (isMountedRef.current) {
      if (page === 1) {
        setHasMore(true);
        getServices();
      } else {
        getMoreServices();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (serviceStartTimestamp && servicePointTimestamp) {
      const time = (servicePointTimestamp - serviceStartTimestamp).toString();
      setStartSecond(time);
    } else {
      setStartSecond("0");
    }
  }, [serviceStartTimestamp, servicePointTimestamp]);

  useEffect(() => {
    setHasMore(true);
    if (page === 1) {
      getServices();
    } else {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDate]);

  // Resets back to the "select a service" placeholder after leaving the page
  useEffect(() => {
    setServiceId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const getMore = () => {
    setPage(page + 1);
  };

  const handleSortByChange = (value) => {
    setSortBy(value);
  };

  const handleDateChange = (date) => {
    setSortDate(date);
  };

  const handlePointTimestamp = (value) => {
    setServicePointTimestamp(value);
  };

  const handleAddResultToResults = (result) => {
    if (
      !services.find((service) => service.SERVICE_KEY === result.SERVICE_KEY)
    ) {
      const newResult = {
        DATE: result.DATE,
        DAY_NIGHT: result.DAY_NIGHT,
        DISABLE_SERVICE: false,
        SERVICE_KEY: result.SERVICE_KEY,
        START_TIMESTAMP: result.service_start_timestamp,
        START_TIMESTAMP_UNIX: result.service_start_timestamp_unix,
        VIMEO_ID: result.VIMEO_ID,
        WEEKDAY: result.WEEKDAY,
        YEAR: result.YEAR,
      };

      const tmpServices = [...services];
      setServices([].concat(tmpServices, [newResult]));
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        paddingTop: "var(--nav-height)",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        fontFamily: theme.typography.fontFamily,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          marginRight: isMobile ? 0 : "400px",
        }}
      >
        <div style={{ padding: isMobile ? "20px 16px 12px" : "28px 32px 16px" }}>
          <h1
            style={{
              fontFamily: theme.typography.secondaryFontFamily,
              fontSize: "min(8vw, 34px)",
              fontWeight: 600,
              color: theme.palette.primary.dark,
              margin: "0 0 8px",
            }}
          >
            {serviceArchiveTitleString(language)}
          </h1>
          <p
            style={{
              fontFamily: theme.typography.paragraphFontFamily,
              fontSize: "14px",
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
              margin: 0,
              maxWidth: "640px",
            }}
          >
            {serviceArchiveSubtitleString(language)}
          </p>
        </div>

        <div style={{ flex: 1, padding: isMobile ? "0 16px 20px" : "0 32px 32px" }}>
          {serviceId && vimeoId ? (
            <div
              style={{
                position: "relative",
                paddingBottom: "56.25%",
                width: "100%",
                backgroundColor: theme.palette.brand.ink,
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {!videoError && isMounted ? (
                <iframe
                  key={vimeoId + startSecond}
                  src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1#t=${startSecond}s`}
                  width="100%"
                  height="100%"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  title="Bethel Bible Church archived service"
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                  playsInline
                  allowFullScreen
                />
              ) : (
                <video
                  loop
                  autoPlay
                  muted
                  playsInline
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  src={language === EN ? unavailable_en : unavailable_ro}
                  alt={unavailableVideoString(language)}
                />
              )}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                aspectRatio: "16 / 9",
                width: "100%",
                backgroundColor: theme.palette.brand.linen,
                border: `1px solid ${theme.palette.brand.border}`,
                borderRadius: "8px",
                color: theme.palette.text.secondary,
              }}
            >
              <VideoLibraryIcon sx={{ fontSize: "40px", color: theme.palette.brand.brass }} />
              <div style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: "14px" }}>
                {serviceArchiveSelectPromptString(language)}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        style={
          isMobile
            ? {
                // Plain document flow (no bounded height / transform) so
                // the page keeps one single, predictable scrollbar -
                // MessagePanel's fixed SERVICES/SEARCH footer stays truly
                // pinned to the real screen bottom, same as it always has.
                // The paddingBottom below just reserves enough room past
                // the panel's content so the Search tab's "Go" button can
                // always be scrolled clear above that fixed footer instead
                // of ending up underneath it.
                width: "100%",
                marginTop: "16px",
                paddingBottom: "120px",
                backgroundColor: theme.palette.brand.linen,
                borderTop: `1px solid ${theme.palette.brand.border}`,
              }
            : {
                position: "fixed",
                top: "var(--nav-height)",
                right: 0,
                width: "400px",
                height: "calc(100vh - var(--nav-height))",
                borderLeft: `1px solid ${theme.palette.brand.border}`,
              }
        }
      >
        {services && (
          <MessagePanel
            from={"services"}
            data={services}
            hasMore={hasMore}
            getMore={getMore}
            sortBy={sortBy}
            handleSortByChange={handleSortByChange}
            sortDate={sortDate}
            handleDateChange={handleDateChange}
            isMobile={isMobile}
            handlePointTimestamp={handlePointTimestamp}
            handleAddResultToResults={handleAddResultToResults}
          />
        )}
        {loadingInitial && <ServiceLoading />}
      </div>
    </div>
  );
};

export default Vault;
